import { FC, ReactNode, useCallback } from 'react';

import { LinkIconPositionsEnum, Loading } from '@argenta/react-ui';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import { t } from '@lingui/macro';

import { I18nLinkProps } from '../../components/I18nLink/I18nLink.types';
import { I18nNavigate } from '../../components/I18nNavigate';
import { VacancyApplyButton } from '../../components/VacanceApplyButton/VacancyApplyButton';
import { VacancyBadges } from '../../components/VacancyBadges';
import { VacancyDetails } from '../../components/VacancyDetails';
import { VacancyDetailsOld } from '../../components/VacancyDetailsOld';
import { VacancyPageTemplate } from '../../components/VacancyPageTemplate';
import { useFetchVacancyDetails } from '../../hooks/api/useFetchVacancyDetails';
import { useAutoRedirect } from '../../hooks/navigation/useAutoRedirect';
import { useGetJobId } from '../../hooks/navigation/useGetJobId';
import { useToggles } from '../../hooks/useToggles';
import { VacanciesRoute } from '../../routers/AppRouter/routes';

export const VacancyDetailsPage: FC = () => {
  const jobId = useGetJobId();
  const { loadingStatus, vacancyDetails } = useFetchVacancyDetails({ jobId });
  const {
    features: { HB7020_vacancy_details_dynamic_content },
  } = useToggles();

  const redirectCondition = useCallback(() => !jobId, [jobId]);
  useAutoRedirect(VacanciesRoute.SEARCH_VACANCIES, redirectCondition);

  const backToOverviewLink: I18nLinkProps = {
    children: t({ id: 'vacancies.detail.backToOverview' }),
    icon: faChevronLeft,
    iconPosition: LinkIconPositionsEnum.LEFT,
    to: VacanciesRoute.SEARCH_VACANCIES,
  };

  const renderBadges = (): ReactNode => {
    if (!vacancyDetails) {
      return undefined;
    }
    return <VacancyBadges schedule={vacancyDetails.schedule} location={vacancyDetails.location} />;
  };

  return (
    <div className="argnt-vacancy-details">
      <VacancyPageTemplate
        title={vacancyDetails?.title}
        returnLinkTop={backToOverviewLink}
        returnLinkBottom={backToOverviewLink}
        underTitleContent={renderBadges()}
        applyButton={<VacancyApplyButton vacancyDetails={vacancyDetails} />}
      >
        <Loading
          loadingStatus={loadingStatus}
          i18nStrings={{ defaultErrorMessage: t({ id: 'vacancies.global.error.defaultMessage' }) }}
          errorComponent={<I18nNavigate to={VacanciesRoute.VACANCY_DETAILS_404} />}
        >
          {vacancyDetails &&
            (HB7020_vacancy_details_dynamic_content ? (
              <VacancyDetails vacancyDetails={vacancyDetails} />
            ) : (
              <VacancyDetailsOld vacancyDetails={vacancyDetails} />
            ))}
        </Loading>
      </VacancyPageTemplate>
    </div>
  );
};
